import { useDispatch } from 'react-redux';
import { useComplexState } from 'hook/helper.hook';
import { getDiffYearsUntilNow } from 'helpers/dateHelper';
import { fetchInsurances } from 'externals/_services/product.service';
import { wrapService } from 'actions/service_wrapper.actions';
import {
  TOBACCO_USE,
  MAJOR_MEDICAL_CONDITION,
} from 'partial/OfferManager/modules/LifeInsuranceModule/helpers/LifeInsurance.helpers';
import { useLogger } from 'hook/error.hook';
import { PLACEMENT } from 'pages/Marketplace/InsuranceMarketplace.types';
import {
  cachePayloadForKey,
  LIFE_INSURANCE_KEY,
} from 'actions/datacache.actions';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import { LIFE_INSURANCE_VERTICAL } from 'types/marketplaceTiles.constants';

const a_fetchMarketplaceLifeInsurances = wrapService(fetchInsurances, {
  name: 'FETCH_LIFE_INSURANCE_MARKETPLACE',
});

export const useLifeInsuranceOcf = props => {
  const logger = useLogger({
    name: 'useLifeInsurance.hook',
    categories: ['marketplace'],
  });

  const dispatch = useDispatch();

  const liConfig = props.config?.marketplace?.li;

  let defaultCoverageType = null;
  let defaultCoverageAmount = null;

  if (liConfig) {
    const age = getDiffYearsUntilNow(props.userInfo?.birthDate);
    if (age >= 56) {
      defaultCoverageType = liConfig.typeOfCoverage[2]?.value;
      defaultCoverageAmount = liConfig.amountOfCoverage[0]?.value;
    } else {
      defaultCoverageType = liConfig?.typeOfCoverage[0]?.value;
      defaultCoverageAmount = liConfig.amountOfCoverage[5]?.value;
    }
  }

  const [states, setStates] = useComplexState({
    zipCode: props.userInfo?.zip,
    currentCoverageType: defaultCoverageType,
    currentCoverageAmount: defaultCoverageAmount,
    multiChoiceAnswers: [],
    fetching: false,
    insuranceResponse: { items: props.ocfOffers },
    showFilters: true,
  });

  const onSelectMultiQuestionOptions = func => {
    setStates({
      multiChoiceAnswers: func(states.multiChoiceAnswers),
    });
  };

  const isInCheckBoxGroup = value => {
    return states.multiChoiceAnswers.includes(value);
  };

  const retriveLifeInsurance = () => {
    let filters = {
      insuranceType: 'LIFE',
      userId: 'me',
      zip: states.zipCode,
      placement: PLACEMENT.offers,
      coverageAmount: states.currentCoverageAmount,
      coverageType: states.currentCoverageType,
      tobaccoUse: isInCheckBoxGroup(TOBACCO_USE),
      majorMedicalCondition: isInCheckBoxGroup(MAJOR_MEDICAL_CONDITION),
    };

    return dispatch(a_fetchMarketplaceLifeInsurances(filters));
  };

  const onLifeInsuranceSearch = () => {
    const mixpanelCheckBox = [];
    states.multiChoiceAnswers.forEach((item, index) => {
      mixpanelCheckBox.push(
        liConfig.checkBoxList.find(e => e.value === item).label,
      );
    });

    setStates({
      fetching: true,
    });
    retriveLifeInsurance()
      .then(insuranceResponse => {
        setStates({
          insuranceResponse: insuranceResponse,
        });
        dispatch(
          cachePayloadForKey(LIFE_INSURANCE_KEY, {
            offers: insuranceResponse?.items,
          }),
        );
        setStates({
          fetching: false,
        });
      })
      .catch(err => {
        logger.reportAPIError(err);
        logger.showError('Unable to retrive offers', err.message);
        setStates({
          fetching: false,
        });
      });
    props.eventHandler(CLICK, {
      'Click Type': 'Update',
      'Coverage Type': liConfig.typeOfCoverage.find(
        e => e.value === states.currentCoverageType,
      ).label,
      'Coverage Amount': states.currentCoverageAmount,
      CheckBox: mixpanelCheckBox,
      Vertical: LIFE_INSURANCE_VERTICAL,
    });
  };

  const handleShowFilters = () => {
    setStates({
      showFilters: !states.showFilters,
    });
  };

  const handleFilterValue = (value, key) => {
    setStates({
      [key]: value,
    });
  };

  return {
    states,
    setStates,
    onLifeInsuranceSearch,
    onSelectMultiQuestionOptions,
    handleShowFilters,
    handleFilterValue,
    checkBoxList: liConfig?.checkBoxList,
    typeOfCoverage: liConfig?.typeOfCoverage,
    amountOfCoverage: liConfig?.amountOfCoverage,
  };
};
